/** @format */

import {ChangeEvent} from 'react';

import {Box, SelectChangeEvent} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import {useMixpanelContext} from 'contexts/MixpanelContext';

import {WIDGETS_LIST} from 'pages/Home/DigitalCast/constants/widgets';
import {trackOverviewEvent} from 'pages/HomeOverview/helpers/trackOverviewEvent';
import {ViewMode} from 'pages/HomeOverview/types';

import {
  AddProjectButton,
  classes,
  ControlGroup,
  ControlLabel,
  HeaderRoot,
  KpiMenuItem,
  KpiSelect,
  SearchClearButton,
  SearchInputBase,
  SearchInputRoot,
  Segment,
  SegmentedToggle,
} from './styles';

interface Props {
  viewMode: ViewMode;
  onChangeViewMode: (mode: ViewMode) => void;
  kpiFilter: string;
  kpiOptions: string[];
  onChangeKpiFilter: (kpi: string) => void;
  search: string;
  onChangeSearch: (value: string) => void;
  onAddProject: () => void;
  isAddDisabled?: boolean;
}

const KpiDropdownIcon = () => (
  <Box
    component='span'
    className='material-symbols-outlined'
    sx={{fontSize: 20, pointerEvents: 'none', position: 'absolute', right: 12}}
  >
    keyboard_arrow_down
  </Box>
);

const Header = ({
  viewMode,
  onChangeViewMode,
  kpiFilter,
  kpiOptions,
  onChangeKpiFilter,
  search,
  onChangeSearch,
  onAddProject,
  isAddDisabled,
}: Props) => {
  const {t} = useTranslation();
  const {mixpanel} = useMixpanelContext();

  const handleClickSegment = (mode: ViewMode) => {
    trackOverviewEvent(mixpanel, 'change_overview_view_mode', {view_mode: mode});
    onChangeViewMode(mode);
  };

  const handleChangeKpi = (event: SelectChangeEvent<unknown>) => {
    const kpi = event.target.value as string;
    trackOverviewEvent(mixpanel, 'change_overview_kpi_filter', {kpi});
    onChangeKpiFilter(kpi);
  };

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    onChangeSearch(e.target.value);
  };

  const handleClearSearch = () => {
    onChangeSearch('');
  };

  const handleAddProject = () => {
    trackOverviewEvent(mixpanel, 'click_overview_add_company_button');
    onAddProject();
  };

  return (
    <HeaderRoot>
      {/* Режим просмотра */}
      <ControlGroup>
        <ControlLabel>
          <Trans>Режим просмотра</Trans>
        </ControlLabel>
        <SegmentedToggle>
          <Segment
            isActive={viewMode === 'dynamic'}
            onClick={() => handleClickSegment('dynamic')}
            data-testid='segment-dynamic'
            aria-pressed={viewMode === 'dynamic'}
          >
            <Box component='span' className={`material-symbols-outlined ${classes.segmentIcon}`}>
              area_chart
            </Box>
            <Box component='span' className={classes.segmentText}>
              <Trans>В динамике</Trans>
            </Box>
          </Segment>
          <Segment
            isActive={viewMode === 'summary'}
            onClick={() => handleClickSegment('summary')}
            data-testid='segment-summary'
            aria-pressed={viewMode === 'summary'}
          >
            <Box component='span' className={`material-symbols-outlined ${classes.segmentIcon}`}>
              table
            </Box>
            <Box component='span' className={classes.segmentText}>
              <Trans>Сводка</Trans>
            </Box>
          </Segment>
        </SegmentedToggle>
      </ControlGroup>

      {/* По KPI */}
      <ControlGroup>
        <ControlLabel>
          <Trans>По KPI</Trans>
        </ControlLabel>
        <KpiSelect
          value={kpiFilter}
          onChange={handleChangeKpi}
          variant='outlined'
          IconComponent={KpiDropdownIcon}
        >
          <KpiMenuItem value='all'>{t('Все KPI', 'Все KPI')}</KpiMenuItem>
          {kpiOptions.map(kpi => (
            <KpiMenuItem key={kpi} value={kpi}>
              {WIDGETS_LIST[kpi].label}
            </KpiMenuItem>
          ))}
        </KpiSelect>
      </ControlGroup>

      {/* Поиск */}
      <SearchInputRoot>
        <Box component='span' className={`material-symbols-outlined ${classes.segmentIcon}`}>
          search
        </Box>
        <SearchInputBase
          value={search}
          onChange={handleChangeSearch}
          placeholder={t('Найти проект', 'Найти проект')}
          inputProps={{'aria-label': t('Найти проект', 'Найти проект')}}
        />
        {search && (
          <SearchClearButton
            onClick={handleClearSearch}
            aria-label={t('Очистить', 'Очистить')}
            data-testid='search-clear'
          >
            <Box component='span' className='material-symbols-outlined'>
              close
            </Box>
          </SearchClearButton>
        )}
      </SearchInputRoot>

      {/* Добавить проект */}
      <AddProjectButton
        disabled={isAddDisabled}
        onClick={handleAddProject}
        data-testid='add-project'
      >
        <Box component='span' className={`material-symbols-outlined ${classes.segmentIcon}`}>
          add
        </Box>
        <Trans>Добавить проект</Trans>
      </AddProjectButton>
    </HeaderRoot>
  );
};

export {Header};
